import React, { useState } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';
import { ApolloError } from '@apollo/client';

const initialContext = {
  setMessage: () => null,
  setSeverity: () => null,
  setDuration: () => null,
  showErrorMessage: () => null,
  showSuccessMessage: () => null,
};

export interface FlashMessageContextType {
  setMessage: (message: string | JSX.Element | null | undefined) => void;
  setSeverity: (severity: AlertColor) => void;
  setDuration: (duration: number | null) => void;
  showErrorMessage: (e: Error | null | ApolloError) => void;
  showSuccessMessage: (m: string | JSX.Element, duration?: number) => void;
}

export const FlashMessageContext = React.createContext<FlashMessageContextType>(initialContext);

export const FlashMessageProvider = ({ children }: { children: React.ReactNode }) => {
  const [message, setMessage] = useState<string | JSX.Element | null | undefined>(null);
  const [severity, setSeverity] = useState<AlertColor>('info');
  const [duration, setDuration] = useState<number | null>(6000);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setMessage(null);
  };

  const showErrorMessage = (error: Error | null | ApolloError) => {
    setMessage(error?.message);
    setSeverity('error');
    setDuration(null);
  };

  const showSuccessMessage = (message: string | JSX.Element, duration = 5_000) => {
    setMessage(message);
    setSeverity('success');
    setDuration(duration);
  };

  return (
    <FlashMessageContext.Provider
      value={{ setMessage, setSeverity, setDuration, showSuccessMessage, showErrorMessage }}
    >
      {children}
      <Snackbar open={!!message} autoHideDuration={duration} onClose={handleClose}>
        <Alert
          onClose={handleClose}
          severity={severity}
          variant="filled"
          elevation={6}
          sx={(theme) => ({
            backgroundColor: theme.palette[severity]['main'],
            color: theme.palette[severity].contrastText,
          })}
          data-cy="snackbar"
        >
          {message}
        </Alert>
      </Snackbar>
    </FlashMessageContext.Provider>
  );
};
